import { useEffect, useRef } from 'react';
import { merchantAPI } from '../lib/api';
import { merchantEvents, MERCHANT_EVENTS } from '../lib/merchantEvents';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../lib/analytics';

interface PaymentToTrack {
    transaction_id: string;
    last_status: string;
}

// Глобальный список платежей для отслеживания
const paymentsToTrack: PaymentToTrack[] = [];

export const useMerchantPaymentPolling = (enabled: boolean = true) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Функция для добавления платежа в отслеживание
    const trackPayment = (transactionId: string, initialStatus: string = 'assigned') => {
        // Проверяем, что платеж еще не отслеживается
        const existing = paymentsToTrack.find(p => p.transaction_id === transactionId);
        if (!existing) {
            paymentsToTrack.push({
                transaction_id: transactionId,
                last_status: initialStatus
            });
            console.log(`📍 Начинаем отслеживание платежа ${transactionId}`);
        }
    };

    // Функция для проверки статусов всех отслеживаемых платежей
    const checkPaymentStatuses = async () => {
        if (paymentsToTrack.length === 0) return;

        console.log(`🔍 Проверяем статусы ${paymentsToTrack.length} платежей`);

        for (const payment of paymentsToTrack) {
            try {
                const statusData = await merchantAPI.getPaymentStatus(payment.transaction_id);

                if (statusData.status !== payment.last_status) {
                    console.log(`🔄 Статус платежа ${payment.transaction_id} изменился: ${payment.last_status} → ${statusData.status}`);

                    const previousStatus = payment.last_status;
                    payment.last_status = statusData.status;
                    trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_STATUS_CHANGED, {
                        source: 'merchant_polling',
                        transaction_id: payment.transaction_id,
                        previous_status: previousStatus,
                        status: statusData.status,
                        payment_id: statusData.payment_id,
                        order_type: statusData.order_type,
                        amount: statusData.amount,
                        environment: statusData.environment || 'live',
                    });

                    // Уведомляем об изменении статуса
                    merchantEvents.emit(MERCHANT_EVENTS.PAYMENT_STATUS_CHANGED, statusData);

                    // Если платеж завершен или отменен, убираем из отслеживания
                    if (['completed', 'cancelled', 'failed'].includes(statusData.status)) {
                        const index = paymentsToTrack.findIndex(p => p.transaction_id === payment.transaction_id);
                        if (index > -1) {
                            paymentsToTrack.splice(index, 1);
                            console.log(`✅ Прекращаем отслеживание платежа ${payment.transaction_id} (статус: ${statusData.status})`);
                        }
                    }
                }
            } catch (error) {
                console.error(`❌ Ошибка проверки статуса платежа ${payment.transaction_id}:`, error);
            }
        }
    };

    useEffect(() => {
        if (!enabled) return;

        // Запускаем polling каждые 10 секунд
        intervalRef.current = setInterval(checkPaymentStatuses, 10000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [enabled]);

    // Возвращаем функцию для добавления платежей в отслеживание
    return {
        trackPayment,
        activePaymentsCount: paymentsToTrack.length
    };
};
